import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Table, 
  Button, 
  Space, 
  Tag, 
  Modal, 
  notification, 
  Card,
  Typography,
  Tooltip,
  Popconfirm
} from 'antd';
import { 
  CheckCircleOutlined, 
  DeleteOutlined,
  ExclamationCircleOutlined,
  UserDeleteOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { UserProps, AuthTokenType } from '../utils/types';
import { getAuthToken } from '../utils/functions';
import { UsersUrl } from '../utils/network';
import axios from 'axios';

const { Text } = Typography;

interface DeniedUsersPanelProps {
  deniedUsers: UserProps[];
  onRefreshUsers: () => void;
  loading: boolean;
  searchTerm?: string;
  selectedRole?: string;
}

const ROLE_CONFIG = {
  Admin: {
    label: "Admin",
    description: "Full system administration access",
    color: "red"
  },
  User: {
    label: "User",
    description: "Standard user access with basic operations",
    color: "blue"
  }
};

const DeniedUsersPanel: React.FC<DeniedUsersPanelProps> = ({ 
  deniedUsers, 
  onRefreshUsers, 
  loading,
  searchTerm = '',
  selectedRole = 'all'
}) => {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [reapprovalModal, setReapprovalModal] = useState<{
    visible: boolean;
    user: UserProps | null;
    assignedRole: string;
    notes: string;
  }>({
    visible: false,
    user: null,
    assignedRole: '',
    notes: ''
  });

  const handleReapprove = (user: UserProps) => {
    setReapprovalModal({
      visible: true,
      user,
      assignedRole: user.role || 'User',
      notes: ''
    });
  };

  const submitReapproval = async () => {
    if (!reapprovalModal.user) return;

    setProcessing(true);
    try {
      const headers = getAuthToken() as AuthTokenType;
      const response = await axios.put(`${UsersUrl}/${reapprovalModal.user.id}`, {
        ...reapprovalModal.user,
        role: reapprovalModal.assignedRole,
        approved: true,
        is_active: true,
        denial_reason: null, // Clear the denial reason
        approval_notes: reapprovalModal.notes
      }, headers);

      if (response) {
        notification.success({
          message: "User Re-approved",
          description: `${reapprovalModal.user.fullname} has been successfully approved and can now access the system.`,
          icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />
        });

        setReapprovalModal({ visible: false, user: null, assignedRole: '', notes: '' });
        onRefreshUsers();
      }
    } catch (error) {
      notification.error({
        message: "Re-approval Error",
        description: "Failed to re-approve user. Please try again."
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteUser = async (user: UserProps) => {
    setProcessing(true);
    try {
      const headers = getAuthToken() as AuthTokenType;
      const response = await axios.delete(`${UsersUrl}/${user.id}`, headers);

      if (response) {
        notification.success({
          message: "User Deleted",
          description: `${user.fullname} has been deleted. They can now submit a new account request.`,
          icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />
        });

        onRefreshUsers();
      }
    } catch (error) {
      notification.error({
        message: "Delete Error",
        description: "Failed to delete user. Please try again."
      });
    } finally {
      setProcessing(false);
    }
  };

  const columns = [
    {
      title: 'User Details',
      key: 'userDetails',
      render: (record: UserProps) => (
        <div>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>
            {record.fullname}
          </div>
          <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
            {record.email}
          </div>
          <div style={{ marginTop: 4 }}>
            <Tag color={ROLE_CONFIG[record.role as keyof typeof ROLE_CONFIG]?.color}>
              {ROLE_CONFIG[record.role as keyof typeof ROLE_CONFIG]?.label}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: 'Denial Reason',
      key: 'denialReason',
      render: (record: UserProps) => (
        <div style={{ maxWidth: 300 }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.denial_reason || 'No reason provided'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Request Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => (
        <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
          {new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: UserProps) => (
        <Space size="small">
          <Tooltip title="Re-approve this user">
            <Button
              size="small"
              type="primary"
              icon={<UndoOutlined />}
              onClick={() => handleReapprove(record)}
              disabled={loading || processing}
            >
              Re-approve
            </Button>
          </Tooltip>
          <Tooltip title="Delete user so they can reapply">
            <Popconfirm
              title="Delete User"
              description={`Are you sure you want to delete ${record.fullname}? They will be able to submit a new account request.`}
              onConfirm={() => handleDeleteUser(record)}
              okText="Yes, Delete"
              cancelText="Cancel"
              icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
            >
              <Button
                size="small"
                icon={<UserDeleteOutlined />}
                disabled={loading || processing}
                danger
              >
                Delete
              </Button>
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Filter denied users based on search and role
  const filteredDeniedUsers = deniedUsers.filter(user => {
    const matchesSearch = !searchTerm || 
      user.fullname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = selectedRole === "all" || user.role === selectedRole;
    
    return matchesSearch && matchesRole;
  });

  return (
    <>
      {/* Denied Requests Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredDeniedUsers}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} denied requests`
          }}
          locale={{
            emptyText: "No denied requests"
          }}
        />
      </Card>

      {/* Re-approval Modal */}
      <Modal
        title="Re-approve User Request"
        open={reapprovalModal.visible}
        onCancel={() => setReapprovalModal({ visible: false, user: null, assignedRole: '', notes: '' })}
        footer={[
          <Button key="cancel" onClick={() => setReapprovalModal({ visible: false, user: null, assignedRole: '', notes: '' })}>
            Cancel
          </Button>,
          <Button 
            key="approve" 
            type="primary" 
            icon={<CheckCircleOutlined />}
            loading={processing}
            onClick={submitReapproval}
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
          >
            Re-approve User
          </Button>
        ]}
      >
        {reapprovalModal.user && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ 
              padding: 16, 
              backgroundColor: '#f5f5f5', 
              borderRadius: 6, 
              marginBottom: 16 
            }}>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>
                {reapprovalModal.user.fullname}
              </div>
              <div style={{ color: '#8c8c8c', marginBottom: 8 }}>
                {reapprovalModal.user.email}
              </div>
              <div style={{ marginBottom: 8 }}>
                <Text strong>Previous Denial Reason:</Text>
                <div style={{ 
                  marginTop: 4, 
                  padding: 8, 
                  backgroundColor: '#fff2f0', 
                  borderRadius: 4,
                  border: '1px solid #ffccc7'
                }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {reapprovalModal.user.denial_reason || 'No reason provided'}
                  </Text>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Assign Role
              </label>
              <select
                value={reapprovalModal.assignedRole}
                onChange={(e) => setReapprovalModal(prev => ({ ...prev, assignedRole: e.target.value }))}
                style={{ 
                  width: '100%', 
                  padding: '8px 12px', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: 6,
                  fontSize: '14px'
                }}
              >
                {Object.entries(ROLE_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>
                    {config.label} - {config.description}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Approval Notes (Optional)
              </label>
              <textarea
                value={reapprovalModal.notes}
                onChange={(e) => setReapprovalModal(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add any notes about this re-approval..."
                style={{ 
                  width: '100%', 
                  minHeight: 80, 
                  padding: '8px 12px', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: 6,
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default DeniedUsersPanel;
